"use client";

import clsx from "clsx";
import Image from "next/image";
import type { MouseEventHandler } from "react";

import type { Category } from "@/types";
import { useDelayedToggleState, useWindowResizeSizing } from "@/lib/hooks";
import { getScrollbarWidth, sanitizePathSegment } from "@/lib/utils";

import NavDropdownItem from "@/app/components/Navigation/components/NavDropdownItem";
import MenuIcon from "@/components/icons/MenuIcon";

type Props = { categories: Category[]; className?: string };

export default function NavServicesButton({ categories, className }: Props) {
	const dropdownMaxHeight = useWindowResizeSizing(0, -100);
	const [hovered, showDropdown, setDropdownVisibility] = useDelayedToggleState(false, 300);

	const setHoverStatus: MouseEventHandler<HTMLDivElement> = (e) => {
		const show = e.type === "mouseenter";
		const root = document.body;
		const scrollbarWidth = getScrollbarWidth();
		
		root.style.overflow = show ? "hidden" : "auto";
		root.style.paddingRight = show ? `${scrollbarWidth}px` : "0";

		setDropdownVisibility(show);
	};

	return (
		<div
			onMouseEnter={setHoverStatus}
			onMouseLeave={setHoverStatus}
			className={clsx("group relative", className)}
		>
			<button
				aria-haspopup="menu"
				aria-controls="services-dropdown"
				aria-expanded={hovered}
				aria-label="Service categories menu"
				className={clsx(
					"flex h-10 items-center gap-3 rounded-[50px] bg-secondary/[0.02] px-8",
					"text-sm2 font-light text-secondary",
					"transition-colors duration-300 hover:bg-tertiary/10 group-hover:bg-tertiary/10",
				)}
			>
				<MenuIcon
					className="w-5 text-foreground"
					aria-hidden
				/>
				<span>Service Categories</span>
			</button>
			<div
				id="services-dropdown"
				className="absolute -left-2 top-4 z-[60]"
				style={{
					display: showDropdown && hovered ? 'block' : 'none',
					pointerEvents: showDropdown && hovered ? 'auto' : 'none',
				}}
			>
				{/* Note(Curstantine): To expand hit area to make the hover work within "spaces" */}
				<div aria-hidden className="h-12" />
				<ul
					className="dropdown-glassmorphism muted-scrollbar inline-grid w-80 overflow-y-auto rounded-2xl"
					style={{ 
						maxHeight: `${dropdownMaxHeight.current}px`,
						border: '1px solid var(--glass-border)',
						boxShadow: 'var(--glass-shadow)',
						background: 'var(--glass-background)',
					}}
				>
						{categories.map((category) => {
							const segment = sanitizePathSegment(category.slug || category._id) || category._id;
							return (
								<NavDropdownItem
									key={category._id}
									title={category.title}
									href={`/categories/${segment}`}
									image={category.logo}
								/>
							);
						})}
					</ul>
			</div>
		</div>
	);
}
